/*
 * Landing core: money formatting, Shopify AJAX cart API helpers and small shared web components
 * (countdown timer, sticky header, mobile menu, modal, "today" date).
 * Everything talks to the real Shopify cart (/cart/add.js, /cart/change.js) and uses the
 * Section Rendering API so the cart drawer HTML always comes from Liquid.
 */
(function () {
  'use strict';

  const config = window.LPConfig || {};
  const routes = config.routes || {};

  /* ------------------------------------------------------------------ money */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = format || config.moneyFormat || '${{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    function withDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }
    const match = fmt.match(placeholder);
    if (!match) return fmt;
    let value = '';
    switch (match[1]) {
      case 'amount': value = withDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      default: value = withDelimiters(cents, 2);
    }
    return fmt.replace(placeholder, value);
  }

  /* ------------------------------------------------------------------ cart */
  function drawer() {
    return document.querySelector('lp-cart-drawer');
  }

  function sectionIds() {
    const d = drawer();
    return d && d.dataset.sectionId ? [d.dataset.sectionId] : [];
  }

  async function postJSON(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) {
      const err = new Error(data.description || data.message || 'Cart request failed');
      err.data = data;
      throw err;
    }
    return data;
  }

  const cart = {
    async add(items, { open = true } = {}) {
      const sections = sectionIds();
      const data = await postJSON(routes.cartAdd || '/cart/add.js', {
        items,
        sections,
        sections_url: window.location.pathname,
      });
      document.dispatchEvent(new CustomEvent('lp:cart:updated', { detail: { sections: data.sections, source: 'add', open } }));
      return data;
    },
    async change(key, quantity) {
      const sections = sectionIds();
      const data = await postJSON(routes.cartChange || '/cart/change.js', {
        id: key,
        quantity,
        sections,
        sections_url: window.location.pathname,
      });
      document.dispatchEvent(new CustomEvent('lp:cart:updated', { detail: { sections: data.sections, cart: data, source: 'change' } }));
      return data;
    },
    async refresh() {
      const ids = sectionIds();
      if (!ids.length) return;
      const url = `${window.location.pathname}?sections=${ids.join(',')}`;
      const res = await fetch(url);
      const sections = await res.json();
      document.dispatchEvent(new CustomEvent('lp:cart:updated', { detail: { sections, source: 'refresh' } }));
    },
    open() {
      const d = drawer();
      if (d && d.open) d.open();
    },
  };

  window.LP = Object.assign(window.LP || {}, { formatMoney, cart, config });

  /* Any element with [data-lp-cart-open] opens the drawer */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-lp-cart-open]');
    if (!trigger) return;
    event.preventDefault();
    cart.open();
  });

  /* Smooth scroll for same-page anchors (header nav, CTAs) with sticky offset */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href*="#"]');
    if (!link || link.hasAttribute('data-lp-no-scroll')) return;
    const url = new URL(link.getAttribute('href'), window.location.href);
    if (url.pathname !== window.location.pathname || !url.hash || url.hash === '#') return;
    const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
    if (!target) return;
    event.preventDefault();
    const menu = link.closest('lp-mobile-menu');
    const go = () => {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      history.pushState(null, '', url.hash);
    };
    if (menu) {
      menu.close();
      setTimeout(go, 250);
    } else {
      go();
    }
  });

  /* ------------------------------------------------------------------ <lp-countdown> */
  class LPCountdown extends HTMLElement {
    connectedCallback() {
      const minutes = parseFloat(this.dataset.minutes || '15');
      this.duration = Math.max(1, minutes) * 60 * 1000;
      this.key = this.dataset.key || 'lp_timer_end';
      this.format = this.dataset.format || 'hh:mm:ss';
      this.endTime = this.readEnd();
      this.tick();
      this.interval = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() {
      clearInterval(this.interval);
    }
    readEnd() {
      let stored = null;
      try { stored = parseInt(sessionStorage.getItem(this.key), 10); } catch (e) {}
      if (!stored || stored <= Date.now()) stored = this.reset();
      return stored;
    }
    reset() {
      const end = Date.now() + this.duration;
      try { sessionStorage.setItem(this.key, String(end)); } catch (e) {}
      return end;
    }
    tick() {
      let remaining = this.endTime - Date.now();
      if (remaining <= 0) {
        this.endTime = this.reset();
        remaining = this.endTime - Date.now();
      }
      const total = Math.floor(remaining / 1000);
      const pad = (n) => String(n).padStart(2, '0');
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      this.textContent = this.format === 'mm:ss' ? `${pad(h * 60 + m)}:${pad(s)}` : `${pad(h)}:${pad(m)}:${pad(s)}`;
    }
  }
  if (!customElements.get('lp-countdown')) customElements.define('lp-countdown', LPCountdown);

  /* ------------------------------------------------------------------ <lp-today> */
  class LPToday extends HTMLElement {
    connectedCallback() {
      const locale = this.dataset.locale || document.documentElement.lang || 'en-US';
      try {
        this.textContent = new Date().toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
      } catch (e) {
        this.textContent = new Date().toDateString();
      }
    }
  }
  if (!customElements.get('lp-today')) customElements.define('lp-today', LPToday);

  /* ------------------------------------------------------------------ <lp-sticky-header> */
  /* Mirrors the reference: after 150px, hide on >50px scroll down, show again on >220px scroll up.
     CSS keeps it always visible on desktop (min-width: 769px). */
  class LPStickyHeader extends HTMLElement {
    connectedCallback() {
      if (this.dataset.hideOnScroll === 'false') return;
      this.last = window.scrollY;
      this.up = 0;
      this.down = 0;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }
    onScroll() {
      const current = window.scrollY;
      const delta = current - this.last;
      if (current <= 150) {
        this.classList.remove('is-hidden', 'is-shown');
        this.up = this.down = 0;
        this.last = current;
        return;
      }
      if (delta > 0) {
        this.down += delta;
        this.up = 0;
        if (this.down > 50) {
          this.classList.add('is-hidden');
          this.classList.remove('is-shown');
          this.down = 0;
        }
      } else if (delta < 0) {
        this.up += Math.abs(delta);
        this.down = 0;
        if (this.up > 220) {
          this.classList.add('is-shown');
          this.classList.remove('is-hidden');
          this.up = 0;
        }
      }
      this.last = current;
    }
  }
  if (!customElements.get('lp-sticky-header')) customElements.define('lp-sticky-header', LPStickyHeader);

  /* ------------------------------------------------------------------ <lp-mobile-menu> */
  class LPMobileMenu extends HTMLElement {
    connectedCallback() {
      this.onKey = (e) => { if (e.key === 'Escape' && this.classList.contains('is-open')) this.close(); };
      this.onOpenClick = (e) => {
        if (e.target.closest(`[aria-controls="${this.id}"]`)) {
          e.preventDefault();
          this.open();
        }
      };
      document.addEventListener('click', this.onOpenClick);
      document.addEventListener('keydown', this.onKey);
      this.querySelectorAll('[data-lp-menu-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.querySelectorAll('a[href]').forEach((a) => {
        a.addEventListener('click', () => {
          const url = new URL(a.getAttribute('href'), window.location.href);
          if (!(url.pathname === window.location.pathname && url.hash)) this.close();
        });
      });
    }
    disconnectedCallback() {
      document.removeEventListener('click', this.onOpenClick);
      document.removeEventListener('keydown', this.onKey);
    }
    open() {
      this.classList.add('is-open');
      document.documentElement.classList.add('lp-lock');
      document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
      const close = this.querySelector('.lp-menu__close');
      if (close) close.focus({ preventScroll: true });
    }
    close() {
      this.classList.remove('is-open');
      document.documentElement.classList.remove('lp-lock');
      document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    }
  }
  if (!customElements.get('lp-mobile-menu')) customElements.define('lp-mobile-menu', LPMobileMenu);

  /* ------------------------------------------------------------------ <lp-modal> */
  class LPModal extends HTMLElement {
    connectedCallback() {
      this.onTrigger = (e) => {
        const t = e.target.closest(`[data-lp-modal-open="${this.id}"]`);
        if (!t) return;
        e.preventDefault();
        this.open();
      };
      this.onKey = (e) => { if (e.key === 'Escape' && this.classList.contains('is-open')) this.close(); };
      document.addEventListener('click', this.onTrigger);
      document.addEventListener('keydown', this.onKey);
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-lp-modal-close]')) this.close();
        const go = e.target.closest('[data-lp-modal-scroll]');
        if (go) {
          e.preventDefault();
          e.stopPropagation();
          this.close();
          setTimeout(() => {
            const target = document.getElementById(go.getAttribute('data-lp-modal-scroll'));
            if (!target) return;
            const top = target.getBoundingClientRect().top + window.scrollY - 24;
            window.scrollTo({ top, behavior: 'smooth' });
          }, 120);
        }
      });
    }
    disconnectedCallback() {
      document.removeEventListener('click', this.onTrigger);
      document.removeEventListener('keydown', this.onKey);
    }
    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('lp-lock');
      const btn = this.querySelector('[data-lp-modal-close].lp-modal-focus, button[data-lp-modal-close]');
      if (btn) btn.focus({ preventScroll: true });
    }
    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('lp-lock');
    }
  }
  if (!customElements.get('lp-modal')) customElements.define('lp-modal', LPModal);

  /* ------------------------------------------------------------------ theme editor helpers */
  document.addEventListener('shopify:block:select', (e) => {
    const modal = e.target.closest && e.target.closest('lp-modal');
    if (modal) modal.open();
  });
})();